/**
 * scrollBackground 模块
 * 
 * 该模块用于实现一个基于 hover或click的背景切换的效果，支持多步切换、防抖处理和动态动画时长控制。
 * 适用于需要逐步切换元素的场景，例如服务卡片、轮播图等。
 * 
 * 主要功能：
 * 1. 根据点击的元素平滑切换背景样式到目标元素。
 * 2. 防抖处理，避免频繁触发切换逻辑。
 * 3. 动态更新 CSS 变量，控制切换动画时长。
 * 4. 支持自定义配置，如总切换时间、防抖等待时间、激活类名等。
 * 
 * 使用示例：
 * const switcher = new ItemSwitcher(".item-loop", {
 *     totalTime: 800, // 总切换时间
 *     debounceWait: 100, // 防抖等待时间
 *     action:"hover", // 触发切换的事件类型，默认为 hover:hover,click
 *     activeClass: "switchered", // 激活状态的类名
 *     switcherClass: "bg-switcher", // 背景的类名
 *     animationDurationVar: "--custom-animation-duration", // 自定义 CSS 变量名
 * });
 * 使用示例：背景逐步无缝切换效果
 * 一、HTML结构：
 <div class="item-loop">
    <div class="item switchered"></div>
    <div class="item"></div>
    <div class="item"></div>
    dots...
    <span class="bg-switcher"></span>
 </div>
 * 二、CSS样式：
 :root {
    --item-loop-duration: 800ms; // 切换动画时长
 }
.item-loop{
    position: relative;
    .bg-switcher{
        display: block;
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
}
 * 定义css变量 --animation-switcher-duration，值为切换动画时长，单位为毫秒
 * 设置三段背景：左右为相同的纯色背景，中间为图片，使用less:
 
 */
import itemStepSwitcher from './_step-switcher';

class backgroundSwitcher {
    constructor(containerSelector, options = {}) {
        // 默认配置
        this.defaultOptions = {
            totalTime: 800, // 总切换时间（单位：毫秒）
            debounceWait: 100, // 防抖等待时间（单位：毫秒）
            action: "click", // 触发切换的事件类型
            activeClass: "switcher-active", // 激活状态的类名
            switcherClass: "bg-switcher", // 背景的类名
            animationDurationVar: "--animation-switcher-duration", // CSS 变量名
            usePseudoElement: "after", // 是否使用伪元素，取值为 false、"before" 或 "after"
            useRem: true, // 是否使用 rem 单位
        };

        // 合并用户自定义配置和默认配置
        this.options = { ...this.defaultOptions, ...options };

        // 验证 usePseudoElement 的取值
        if (
            this.options.usePseudoElement !== false &&
            this.options.usePseudoElement !== "before" &&
            this.options.usePseudoElement !== "after"
        ) {
            console.error(
                `usePseudoElement 的取值无效，应为 false、"before" 或 "after"，当前值为: ${this.options.usePseudoElement}`
            );
            return;
        }

        // 获取容器元素
        this.container = document.querySelector(containerSelector);
        if (!this.container) {
            console.error(`容器元素未找到: ${containerSelector}`);
            return;
        }

        // 为容器生成唯一标识符
        this.containerId = this.container.id || `container-${Math.random().toString(36).substr(2, 9)}`;
        this.container.id = this.containerId; // 确保容器有唯一的 id

        // 获取所有可切换的子元素
        this.items = this.container.querySelectorAll(".item");
        if (!this.items.length) {
            console.error(`未找到可切换的子元素: .item`);
            return;
        }

        // 获取背景元素（指定类模式）
        this.switcher = this.container.querySelector(`.${this.options.switcherClass}`);

        // 初始化 itemStepSwitcher
        this.stepSwitcher = new itemStepSwitcher(containerSelector, {
            totalTime: this.options.totalTime,
            debounceWait: this.options.debounceWait,
            action: this.options.action,
            activeClass: this.options.activeClass,
            animationDurationVar: this.options.animationDurationVar,
        });

        // 初始化事件监听
        this._initEvents();

        // 初始化激活状态
        this._initializeActiveState();
    }

    /**
     * 初始化激活状态
     */
    _initializeActiveState() {
        // 查找当前激活的元素
        const activeItem = this.container.querySelector(`.${this.options.activeClass}`);

        if (activeItem) {
            // 如果存在激活的元素，执行一次背景切换
            this._switchBackground(activeItem);
        } else if (this.items.length > 0) {
            // 如果没有激活的元素，默认激活第一个元素
            //this._switchBackground(this.items[0]);
        }
    }

    /**
     * 初始化事件监听
     */
    _initEvents() {
        const actions = this.options.action.split(",");
        actions.forEach((action) => {
            this.items.forEach((item) => {
                if (action === "hover") {
                    item.addEventListener("mouseenter", this._handleSwitch.bind(this));
                } else if (action === "click") {
                    item.addEventListener("click", this._handleSwitch.bind(this));
                }
            });
        });
    }

    /**
     * 处理切换逻辑
     * @param {Event} event - 触发的事件
     */
    _handleSwitch(event) {
        // 防抖处理
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this._switchBackground(event.target);
        }, this.options.debounceWait);
    }

    /**
     * 切换背景
     * @param {HTMLElement} target - 目标元素
     */
    _switchBackground(target) {
        // 获取目标元素的索引
        const targetIndex = Array.from(this.items).indexOf(target);

        // 获取当前激活元素的索引
        const activeItem = this.container.querySelector(`.${this.options.activeClass}`);
        const activeIndex = activeItem ? Array.from(this.items).indexOf(activeItem) : -1;

        // 调用 itemStepSwitcher 的 switchItem 方法，逐步切换 activeClass
        this.stepSwitcher.switchItem($(target), targetIndex, activeIndex);

        // 获取目标元素的位置和尺寸
        const targetRect = target.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();

        // 计算目标元素相对于容器的位置
        const left = targetRect.left - containerRect.left - 1;
        const top = targetRect.top - containerRect.top - 1;
        const width = targetRect.width;
        const height = targetRect.height;

        // 更新背景样式
        if (this.options.usePseudoElement === "before") {
            // 使用 ::before 伪元素
            this._updatePseudoElement("before", left, top, width, height);
        } else if (this.options.usePseudoElement === "after") {
            // 使用 ::after 伪元素
            this._updatePseudoElement("after", left, top, width, height);
        } else {
            // 使用指定类
            this._updateSwitcher(left, top, width, height);
        }

        // 更新 CSS 变量，控制动画时长
        this.container.style.setProperty(
            this.options.animationDurationVar,
            `${this.options.totalTime}ms`
        );
    }

    /**
     * 将 px 转换为 rem
     * @param {number} px - 像素值
     * @returns {string} - rem 值
     */
    _pxToRem(px) {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        return `${px / rootFontSize}rem`;
    }

    /**
     * 更新伪元素样式
     * @param {string} pseudo - 伪元素类型（"before" 或 "after"）
     * @param {number} left - 左侧位置
     * @param {number} top - 顶部位置
     * @param {number} width - 宽度
     * @param {number} height - 高度
     */
    _updatePseudoElement(pseudo, left, top, width, height) {
        // 生成唯一的 <style> 标签 id
        const styleId = `pseudo-style-${this.containerId}-${pseudo}`;

        // 查找或创建 <style> 标签
        let styleTag = document.getElementById(styleId);
        if (!styleTag) {
            styleTag = document.createElement("style");
            styleTag.id = styleId;
            document.head.appendChild(styleTag);
        }

        // 将 px 转换为 rem（如果启用）
        const leftValue = this.options.useRem ? this._pxToRem(left) : `${left}px`;
        const topValue = this.options.useRem ? this._pxToRem(top) : `${top}px`;
        const widthValue = this.options.useRem ? this._pxToRem(width) : `${width}px`;
        const heightValue = this.options.useRem ? this._pxToRem(height) : `${height}px`;

        // 更新伪元素样式
        styleTag.textContent = `
            #${this.containerId}::${pseudo} {
                left: ${leftValue};
                top: ${topValue};
                width: ${widthValue};
                height: ${heightValue};
            }
        `;
    }

    /**
     * 更新指定类样式
     * @param {number} left - 左侧位置
     * @param {number} top - 顶部位置
     * @param {number} width - 宽度
     * @param {number} height - 高度
     */
    _updateSwitcher(left, top, width, height) {
        if (this.switcher) {
            // 将 px 转换为 rem（如果启用）
            const leftValue = this.options.useRem ? this._pxToRem(left) : `${left}px`;
            const topValue = this.options.useRem ? this._pxToRem(top) : `${top}px`;
            const widthValue = this.options.useRem ? this._pxToRem(width) : `${width}px`;
            const heightValue = this.options.useRem ? this._pxToRem(height) : `${height}px`;

            this.switcher.style.left = leftValue;
            this.switcher.style.top = topValue;
            this.switcher.style.width = widthValue;
            this.switcher.style.height = heightValue;
        }
    }
}

// 导出 backgroundSwitcher 类
export default backgroundSwitcher;